import type { ProjectChartDatapoint } from '@/api/generated/model';

/** Leading pan buckets are all-null or all-zero across every dataset. */
function isPanEmptyValue(y: number | undefined): boolean {
  if (y == null || !Number.isFinite(y)) return true;
  return y === 0;
}

function isBucketEmpty(chart: ProjectChartDatapoint, index: number): boolean {
  const datasets = chart.datasets ?? [];
  if (datasets.length === 0) return true;

  return datasets.every((dataset) => isPanEmptyValue(dataset.data[index]?.y));
}

/** Remove leading buckets where every dataset value is empty. Trailing buckets are kept. */
export function trimChartEmptyPan(chart: ProjectChartDatapoint): ProjectChartDatapoint {
  const buckets = chart.x_label_values ?? [];
  const datasets = chart.datasets ?? [];

  if (buckets.length === 0) {
    return { x_label_values: [], datasets: datasets.map((dataset) => ({ ...dataset, data: [] })) };
  }

  let start = 0;
  while (start < buckets.length && isBucketEmpty(chart, start)) {
    start += 1;
  }

  if (start >= buckets.length) {
    return { x_label_values: [], datasets: datasets.map((dataset) => ({ ...dataset, data: [] })) };
  }

  return {
    x_label_values: buckets.slice(start),
    datasets: datasets.map((dataset) => ({
      ...dataset,
      data: dataset.data.slice(start),
    })),
  };
}

export function countTrimmedChartBuckets(chart: ProjectChartDatapoint | undefined): number {
  if (!chart) return 0;
  return trimChartEmptyPan(chart).x_label_values?.length ?? 0;
}

/** First and last bucket indices that contain at least one non-empty dataset value. */
export function getChartNonEmptyBucketSpan(
  chart: ProjectChartDatapoint | undefined,
): { startIndex: number; endIndex: number } | null {
  const buckets = chart?.x_label_values ?? [];
  if (buckets.length === 0 || !chart) return null;

  let startIndex = -1;
  let endIndex = -1;

  for (let index = 0; index < buckets.length; index += 1) {
    if (!isBucketEmpty(chart, index)) {
      if (startIndex === -1) startIndex = index;
      endIndex = index;
    }
  }

  if (startIndex === -1 || endIndex === -1) return null;
  return { startIndex, endIndex };
}
